import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, uploadFile } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { todayInWarsaw } from '../events/calendar';
import { missingText, TodoDot } from './TodoDot';
import { CATALOG_KEY, categoryLabels, KIND_LABELS, useCategories, useCustomFieldDefs, useLocations, type Item } from './ItemForm';
import { formatPln } from './money';
import { UnitsTable, type Unit } from './UnitsTable';
import { atDefault, bpToInput, dayLabel, suggestedRental, warrantyState } from './whereNow';
import { seesSupplierOwners, useSupplierOwners } from './SupplierOwners';
import { BlocksSection } from '../availability/Blocks';

/** The API still sends `alternativePrices` (kept in the database): not shown since owner 2026-10-24 11:24. */
type Detail = Item & { units: Unit[]; documents: { fileId: string; name: string }[] };

function Problem({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="alert" role="alert">
      {error.message}
    </p>
  );
}

function DocumentsSection({ item, canEdit, canUpload, canDownload }: { item: Detail; canEdit: boolean; canUpload: boolean; canDownload: boolean }) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fileId = await uploadFile(file);
      return api(`/api/catalog/items/${item.id}/documents`, { method: 'POST', body: { fileId } });
    },
    onSettled: refresh,
  });
  const detach = useMutation({ mutationFn: (fileId: string) => api(`/api/catalog/items/${item.id}/documents/${fileId}`, { method: 'DELETE' }), onSettled: refresh });
  return (
    <section aria-label="Dokumentacja" className="dsec" data-feature-id="catalog.documents">
      <h3>Dokumentacja</h3>
      {item.documents.length === 0 && <p className="muted">Brak dokumentów.</p>}
      <ul className="list">
        {item.documents.map((d) => (
          <li key={d.fileId} className="row">
            <div className="row-main">{canDownload ? <a href={`/api/files/${d.fileId}`} download={d.name}>{d.name}</a> : d.name}</div>
            {canEdit && (
              <button className="btn small" type="button" aria-label={`Odepnij dokument: ${d.name}`} onClick={() => detach.mutate(d.fileId)}>
                Odepnij
              </button>
            )}
          </li>
        ))}
      </ul>
      <Problem error={detach.error} />
      {canEdit && canUpload && (
        <div className="inline-form">
          <Problem error={upload.error} />
          <label className="field">
            <span>Plik dokumentacji</span>
            <input
              type="file"
              disabled={upload.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) upload.mutate(file);
              }}
            />
          </label>
          {upload.isPending && <p className="muted" role="status">Wgrywanie…</p>}
          <p className="hint">Dozwolone: pdf, jpg, png, heic, webp, docx, xlsx, csv, txt (do 25 MB).</p>
        </div>
      )}
    </section>
  );
}

function dimensions(item: Item): string | null {
  const parts = [item.lengthMm, item.widthMm, item.heightMm].filter((v): v is number => v != null);
  if (parts.length === 0) return null;
  const cm = parts.every((v) => v % 10 === 0);
  return `${parts.map((v) => (cm ? v / 10 : v)).join(' × ')} ${cm ? 'cm' : 'mm'}`;
}

type Row = [string, ReactNode];

/** One group of the data: a small heading and compact label / value rows; empty groups are left out. */
function DataGroup({ title, rows, children }: { title: string; rows: Row[]; children?: ReactNode }) {
  if (rows.length === 0 && !children) return null;
  return (
    <div className="dgroup">
      <h3>{title}</h3>
      {rows.length > 0 && (
        <dl className="data-list">
          {rows.map(([label, value]) => (
            <div key={label} className="data-row">
              <dt className="muted">{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {children}
    </div>
  );
}

/** "Gdzie teraz": the default location gets the rest; other places and rentals line by line. */
function WhereNow({ item, names }: { item: Item; names: ReadonlyMap<string, string> }) {
  const lines = item.placements ?? [];
  const home = `${(item.defaultLocationId && names.get(item.defaultLocationId)) || 'Domyślna lokalizacja'} (domyślna)`;
  const rest = atDefault(item.totalQuantity, lines);
  const rows: { key: string; qty: number; label: string }[] = [
    ...(rest > 0 || lines.length === 0 ? [{ key: 'home', qty: rest, label: home }] : []),
    ...lines.map((l, i) => ({
      key: String(i),
      qty: l.quantity,
      label:
        l.place === 'location'
          ? (names.get(l.locationId ?? '') ?? 'Inna lokalizacja')
          : ['Wypożyczone', [l.rentedTo, l.rentedUntil ? `do ${dayLabel(l.rentedUntil)}` : ''].filter(Boolean).join(', ')].filter(Boolean).join(': '),
    })),
  ];
  return (
    <ul className="where-list" aria-label="Gdzie teraz">
      {rows.map((r) => (
        <li key={r.key}>
          <span className="where-qty">{r.qty} szt.</span>
          <span>{r.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** „Ceny u dostawców” (owner 2026-10-24 11:24: in place of „Ceny alternatywne”): „Mają to”, only when there is something. */
function SuppliersSection({ itemId, show }: { itemId: string; show: boolean }) {
  const owners = useSupplierOwners(itemId, show);
  if (!owners) return null;
  return (
    <section aria-label="Ceny u dostawców" className="dsec" data-feature-id="subrentals.catalog_match">
      <h3>Ceny u dostawców (za dobę)</h3>
      {owners}
    </section>
  );
}

/** Read-only data of the item, in the groups of the form: only what the server sent. */
function DataSection({ item, canViewCategories }: { item: Item; canViewCategories: boolean }) {
  const categories = useCategories(canViewCategories).data?.categories ?? [];
  const defs = useCustomFieldDefs().data?.customFields ?? [];
  const locations = useLocations().data?.locations ?? [];
  const names = new Map(locations.map((l) => [l.id, l.name]));
  const category = item.categoryId ? categoryLabels(categories).find((c) => c.id === item.categoryId)?.label : undefined;
  const money = (grosze: number | null | undefined) => (grosze == null ? null : formatPln(grosze));
  const rows = (pairs: [string, ReactNode | null | undefined][]): Row[] => pairs.filter((p): p is Row => p[1] !== null && p[1] !== undefined && p[1] !== '');

  const product = rows([
    ['Producent / model', [item.manufacturer, item.model].filter(Boolean).join(' ')],
    ['Kategoria', category],
    [
      'Podpowiadaj też w',
      (item.suggestCategoryIds ?? [])
        .map((id) => categoryLabels(categories).find((c) => c.id === id)?.label)
        .filter(Boolean)
        .join(', '),
    ],
    ['Rodzaj', KIND_LABELS[item.kind]],
    ['Opis i uwagi', item.notes],
  ]);
  const suggestion = suggestedRental(item.purchasePriceGrosze, item.rentalFactorBp);
  const prices = rows([
    ['Docelowa cena za dobę', money(item.rentalPriceDayGrosze)],
    ['Współczynnik wynajmu', item.rentalFactorBp == null ? null : `${bpToInput(item.rentalFactorBp)}%`],
    ['Sugerowana cena za dobę', money(suggestion)],
    ['Cena zakupu', money(item.purchasePriceGrosze)],
    ['Wartość', money(item.valueGrosze)],
  ]);
  const params = rows([
    ['Waga', item.weightG == null ? null : `${item.weightG / 1000} kg`.replace('.', ',')],
    ['Wymiary', dimensions(item)],
    ...defs.map((def): [string, string | undefined] => {
      const value = item.customFields?.[def.key];
      return [def.label, value === undefined ? undefined : typeof value === 'boolean' ? (value ? 'Tak' : 'Nie') : String(value)];
    }),
  ]);
  const warranty = warrantyState(item.warrantyUntil, todayInWarsaw());
  const purchase = rows([
    ['Data zakupu', item.purchaseDate ? dayLabel(item.purchaseDate) : null],
    ['Faktura', item.purchaseSource ? <Link to={`/finanse/zakupy/${item.purchaseSource.id}`}>Faktura: {item.purchaseSource.label}</Link> : null],
    [
      'Gwarancja do',
      item.warrantyUntil ? (
        <>
          {dayLabel(item.warrantyUntil)} {warranty && <span className={`chip ${warranty.kind === 'expired' ? 'sensitive' : 'accent'}`}>{warranty.label}</span>}
        </>
      ) : null,
    ],
  ]);
  const stock = rows([
    ['Ilość', String(item.totalQuantity)],
    ['Dostępne teraz', String(item.ownedQuantity)],
    ['Minimalny stan', item.kind === 'consumable' && item.minStock != null ? String(item.minStock) : null],
  ]);
  return (
    <section aria-label="Dane" className="dsec dsec-data">
      {item.missingFields && item.missingFields.length > 0 && (
        <p className="todo-line">
          <TodoDot missing={item.missingFields} />
          Do uzupełnienia. {missingText(item.missingFields)}.
        </p>
      )}
      <div className="dgrid">
        <DataGroup title="Dane produktu" rows={product} />
        <DataGroup title="Ceny" rows={prices} />
        <DataGroup title="Parametry" rows={params} />
        <DataGroup title="Zakup i gwarancja" rows={purchase} />
        <DataGroup title="Ilość i lokalizacja" rows={stock}>
          {item.placements !== undefined ? (
            <WhereNow item={item} names={names} />
          ) : (
            item.defaultLocationId && (
              <dl className="data-list">
                <div className="data-row">
                  <dt className="muted">Domyślna lokalizacja</dt>
                  <dd>{names.get(item.defaultLocationId)}</dd>
                </div>
              </dl>
            )
          )}
        </DataGroup>
      </div>
    </section>
  );
}

/** Units, comparison prices and documents of one item. */
/** Owner 2026-10-22: no "Duplikuj" button here — it is in the row's "…" menu. */
export function ItemDetails({ itemId }: { itemId: string }) {
  const { me } = useMe();
  const detail = useQuery({ queryKey: [...CATALOG_KEY, 'item', itemId], queryFn: () => api<Detail>(`/api/catalog/items/${itemId}`) });
  const locations = useLocations().data?.locations ?? [];
  if (!me) return null;
  if (detail.error) return <p className="alert" role="alert">{detail.error instanceof ApiError ? detail.error.message : 'Nie udało się wczytać pozycji'}</p>;
  const item = detail.data;
  if (!item) return <p className="muted" role="status">Wczytywanie…</p>;
  const canEdit = can(me, 'catalog.items', 'edit');
  const home = (item.defaultLocationId && locations.find((l) => l.id === item.defaultLocationId)?.name) || 'Domyślna lokalizacja';
  return (
    <div className="item-details" aria-label={`Szczegóły: ${item.name}`} role="group">
      <DataSection item={item} canViewCategories={can(me, 'catalog.categories', 'view')} />
      {item.kind === 'serialized' && <UnitsTable itemId={item.id} units={item.units} canEdit={canEdit} canPlace={fieldVisible(me, 'catalog.items', 'placements')} defaultLocationName={home} />}
      <BlocksSection itemId={item.id} itemName={item.name} />
      <SuppliersSection itemId={item.id} show={seesSupplierOwners(me)} />
      <DocumentsSection item={item} canEdit={canEdit} canUpload={can(me, 'files.files', 'create')} canDownload={can(me, 'files.files', 'view')} />
    </div>
  );
}
